import type { GameView, HandView } from "@numbet/engine";
import type { ClientMessage } from "@numbet/protocol";
import { formatChips, formatWithUnit, secondsLeft } from "../format.ts";
import { joinNames, t } from "../i18n.ts";
import { NumberLine, type LineEntry } from "./NumberLine.tsx";

interface ResultProps {
  view: GameView;
  hand: HandView;
  myId: string;
  now: number;
  send: (message: ClientMessage) => void;
}

export function Result({ view, hand, myId, now, send }: ResultProps) {
  const result = hand.result;
  if (!result) return null;
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? "?";
  const { unit, decimals } = hand.question;
  const me = view.players.find((p) => p.id === myId);
  const winners = new Set(result.pots.flatMap((p) => p.winners));
  const lastHand = view.handsPlayed >= view.handsPerGame;
  const seconds = hand.deadline !== null ? secondsLeft(hand.deadline, now) : null;

  const entries: LineEntry[] = view.players.flatMap((p) =>
    p.inHand && p.estimate !== null ? [{ id: p.id, name: p.name, estimate: p.estimate, winner: winners.has(p.id) }] : [],
  );
  const canShow = me?.inHand && me.hasEstimate && !me.estimatePublic;

  return (
    <section className="result" aria-label="Ergebnis" aria-live="polite">
      {result.kind === "noContest" ? (
        <div className="result-title">{t.noContest}</div>
      ) : (
        result.pots.map((pot, i) => {
          const names = pot.winners.map(name);
          const amount = formatChips(pot.amount);
          const prefix = result.pots.length > 1 ? `${i === 0 ? t.pot : t.sidePot(i)}: ` : "";
          const text =
            result.kind === "uncontested"
              ? t.winsUncontested(names[0] ?? "", amount)
              : names.length > 1
                ? t.splitPot(joinNames(names), amount)
                : t.winsPot(names[0] ?? "", amount);
          return (
            <div key={i} className="result-title">
              {prefix}
              {text}
            </div>
          );
        })
      )}

      {hand.answer !== null && entries.length === 0 && (
        <div>
          <span className="label">{t.theAnswerWas}: </span>
          <strong className="num">{formatWithUnit(hand.answer, unit, decimals)}</strong>
        </div>
      )}

      {hand.answer !== null && entries.length > 0 && <NumberLine answer={hand.answer} unit={unit} decimals={decimals} entries={entries} />}

      <div className="row" style={{ justifyContent: "center" }}>
        {canShow && (
          <button type="button" className="btn btn-small btn-outline" onClick={() => send({ type: "showEstimate" })}>
            {t.showEstimate}
          </button>
        )}
        {seconds !== null && view.phase === "playing" && (
          <span className="timer-text">{lastHand ? t.finalResultIn(seconds) : t.nextHandIn(seconds)}</span>
        )}
      </div>
    </section>
  );
}
